"use client";

import { getCountryCodeFromLocale } from "@/apiUtils/commonUtils";
import { doctormChatBotMappings, sessionQueryParams } from "@/apiUtils/constants";
import CartIcon from "@/assets/icons/cart.svg?react";
import FavoriteIcon from "@/assets/icons/favorite.svg?react";
import HamIcon from "@/assets/icons/hamburger.svg?react";
import SearchIcon from "@/assets/icons/search.svg?react";
import { HeaderContent, HeaderDynamicBannerContent, SocialMediaIcon } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { NavCtx } from "@/providers/NavProvider";
import { useGlobalStore } from "@/store/store";
import {
  debounce,
  getFieldValue,
  getLangFromLocale,
  isInHomePage,
  isNonTransactionalCountry,
} from "@/utils/common";
import { useParams, usePathname } from "next/navigation";
import Script from "next/script";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CLink } from "../common/customLink/CustomLink";
import DynamicBanner from "../dynamicBanner/DynamicBanner";
import LocaleSelectorOverlay from "../home/localeSelectorOverlay/LocaleSelectorOverlay";
import { NavDesktopMenu } from "./navDesktopMenu/NavDesktopMenu";
import NavMobileMenu from "./navMobileMenu/NavMobileMenu";
import OnceAnimatedBrandLogo from "./OnceAnimatedBrandLogo";
import ProfileIcon from "./ProfileIcon";
import DesktopSuggesationMenu from "./searchBar/DesktopSuggestionMenu";
import MobileSearchMenu from "./searchBar/MobileSearchMenu";
import SearchBar from "./searchBar/SearchBar";
import { createSignal } from "@/utils/signal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Button from "../ui/button/Button";
import useCRouter from "@/hooks/useCRouter";
import { NavReorderLenses } from "./NavReorderLenses";

const OFFSET = 150;
const FLY_MENU_OFFSET = 20;
const ANIMATION = "ease-linear";
const DURATION = "duration-200";

interface IProps {
  headerContent: HeaderContent;
  translation: Record<string, string>;
  // socialProviders: readonly AvailableSocialLogins[]
  enableVto: boolean;
  dynamicBanner?: HeaderDynamicBannerContent;
  socialMediaIcons?: SocialMediaIcon[];
}

export const pdpEditsSignal = createSignal<boolean>(false);

const Navbar = ({
  headerContent,
  translation,
  enableVto,
  dynamicBanner,
  socialMediaIcons,
}: IProps) => {

  // states
  const [isScrolling, setIsScrolling] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [showMobileMav, setShowMobileNav] = useState(false);
  const [isLinkHovered, setIsLinkHovered] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const suggestionRef = useRef<HTMLDivElement>(null);
  const [hideFlyMenu, setHideFlyMenu] = useState(false);

  // console.log("logins", {
  //   apple: headerContent?.enableAppleLogin,
  //   google: headerContent?.enableGoogleLogin,
  //   facebook: headerContent?.enableFacebookLogin,
  // });

  // consts
  const { dynamicBanners, headerModules } = headerContent ?? {
    dynamicBanners: [],
    headerModules: [],
  };
  // const isDynamicHeaderShown = false;
  const isDynamicHeaderShown = dynamicBanner && dynamicBanner?.items?.length > 0;

  // hooks
  const lastScrollPos = useRef(OFFSET);
  const lastFlyMenuHidePos = useRef(0);
  const pathname = usePathname();
  const { locale } = useParams<{ locale: string }>();
  const lang = getLangFromLocale(locale as string);
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 300),
    [],
  );
  const { cartCount, isAuthenticated, user } = useGlobalStore();
  const isHomePage = useMemo(() => isInHomePage(pathname), [pathname]);
  const isSmallScreen = useMediaQuery("(max-width:768px)", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const { push } = useCRouter();
  const isNonTransactional = useMemo(() => isNonTransactionalCountry(locale as string), [locale]);

  // A little hack. @font-face not working.
  // const is29LTBukraFont = dynamicBanners?.fontFace?.trim()
  //   ? fontLTBukraRegex.test(dynamicBanners.fontFace.trim().toLowerCase())
  //   : false;

  // methods
  function handleShowMobileNav() {
    setShowMobileNav(true);
    sessionStorage.setItem("showMobileNav", String(true));
  }

  function handleHideMobileNav() {
    setShowMobileNav(false);
    sessionStorage.clear();
  }

  function handleSetFlyMenuState(val: boolean) {
    setHideFlyMenu(val);
  }

  function handleToggleBottomNavbar() {
    const curScrollPos = document.documentElement.scrollTop;

    if (curScrollPos === 0) {
      setIsAtTop(true);
    } else {
      setIsAtTop(false);
    }

    if (curScrollPos > lastScrollPos.current) {
      setIsScrolling(true);
    } else {
      if (!pdpEditsSignal.getSignal()) setIsScrolling(false);
    }

    if (curScrollPos > lastFlyMenuHidePos.current) {
      setHideFlyMenu(true);
    } else {
      setHideFlyMenu(false);
    }

    lastScrollPos.current = curScrollPos <= OFFSET ? OFFSET : curScrollPos;
    lastFlyMenuHidePos.current = curScrollPos + FLY_MENU_OFFSET;
  }

  function handleGetHeaderClasses() {
    if (isInHomePage(pathname)) {
      if (isAtTop && !isScrolling) return "bg-transparent text-white h-[108px] md:h-[140px]";
      if (!isAtTop && !isScrolling) return "h-[108px] md:h-[140px]";
      if (!isAtTop && isScrolling) return "h-[54px] md:h-[80px]";
    } else {
      if (isAtTop && !isScrolling) return "h-[108px] md:h-[140px]";
      if (!isAtTop && !isScrolling) return "h-[108px] md:h-[140px]";
      if (!isAtTop && isScrolling) return "h-[54px] md:h-[80px]";
    }
    2;
  }

  function handleGetHeaderBgClasses(): string {
    if (isInHomePage(pathname)) {
      if (isAtTop) return "block h-[120px] md:h-[200px]";
      return "hidden";
    } else return "hidden";
  }

  function handleGetNavUpperClasses(): string {
    return "h-[54px] md:h-[80px]";
  }

  function handleIsLinkHovered(isHovered: boolean) {
    setIsLinkHovered(isHovered);
  }

  function isPrimaryTextColorEnabled() {
    if (isHomePage) {
      if (isLinkHovered) return false;
      if (isAtTop && !isScrolling) return true;
      if (!isScrolling && !isAtTop) return false;
      if (!isScrolling) return false;
      else return false;
    }
    return false;
  }

  function handleManagePlpEditsSection() {
    const breadcrumb = document.getElementById("breadcrumb-section");
    const edits = document.getElementById("plp-edit-section");
    const filters = document.getElementById("filter-section");
    const dynamicBannerHeight =
      (document.querySelector(`#dynamic-banner`) as HTMLDivElement)?.offsetHeight ?? 0;

    // Header height based on scroll direction:
    // isScrolling=true  → scrolling down → header compressed
    // isScrolling=false → scrolling up   → header expanded
    const headerHeight = isSmallScreen ? (isScrolling ? 50 : 101) : isScrolling ? 65 : 129;

    if (breadcrumb) {
      if (isScrolling) {
        breadcrumb.style.top = `-${breadcrumb.offsetHeight}px`;
      } else {
        breadcrumb.style.top = `${headerHeight + dynamicBannerHeight}px`;
      }
    }

    const breadcrumbHeight = breadcrumb?.offsetHeight ?? 0;
    const breadcrumbTop = isScrolling
      ? -(breadcrumbHeight)
      : headerHeight + dynamicBannerHeight;
    const breadcrumbBottom = breadcrumbTop + breadcrumbHeight;
     if (isSmallScreen && !isScrolling) {
    if (filters) filters.style.display = "none";
    if (edits) edits.style.display = "none";
    return;
  } else {
    if (filters) filters.style.display = "";
    if (edits) edits.style.display = "";
  }

    if (edits) {
      const editsTop = breadcrumbBottom;
      const editsHeight = edits.offsetHeight || (isSmallScreen ? 102 : 110);
      const filtersTop = editsTop + editsHeight;

      edits.style.top = `${editsTop}px`;

      if (filters) filters.style.top = `${filtersTop}px`;
    } else {
      if (filters) filters.style.top = `${breadcrumbBottom}px`;
    }
  }

  // lifecycle
  useEffect(() => {
    document.addEventListener("scroll", handleManagePlpEditsSection);
    return () => document.removeEventListener("scroll", handleManagePlpEditsSection);
  }, [isScrolling, isDynamicHeaderShown]);

  useEffect(() => {
    setShowSearch(false);
    setShowMobileSearch(false);

    handleToggleBottomNavbar();
    document.addEventListener("scroll", handleToggleBottomNavbar);
    return () => document.removeEventListener("scroll", handleToggleBottomNavbar);
  }, []);

  useEffect(() => {
    const showMobileViewNav = sessionStorage.getItem("showMobileNav");
    if (showMobileViewNav) {
      setShowMobileNav(Boolean(showMobileViewNav));
    }
  }, []);

  useEffect(() => {
    if (window && typeof window !== "undefined" && window.fcWidget) {
      window.fcWidget.show();
    }
  }, []);

  return (
    <>
      <Script
        id="freshchat-js-sdk"
        strategy="lazyOnload"
        src="https://magrabi1.freshchat.com/js/widget.js"
        onLoad={() => {
          let chatbotConfig:
            | {
                token: string;
                host: string;
                widgetUuid: string;
                tag: string[];
              }
            | string = "";

          try {
            chatbotConfig = JSON.parse(process.env.NEXT_PUBLIC_CHAT_BOT_CONFIG ?? "");
          } catch (err) {
            console.log(err);
          }

          function initFreshChat() {
            if (typeof chatbotConfig === "string") return;

            window.fcWidget.init({
              token: chatbotConfig.token,
              host: chatbotConfig.host,
              widgetUuid: chatbotConfig.widgetUuid,
              tags: [doctormChatBotMappings[getCountryCodeFromLocale(locale)]],
            });
          }

          initFreshChat();
        }}
      />
      {headerContent?.localeSelectorOverlay && (
        <LocaleSelectorOverlay locale={locale} data={headerContent?.localeSelectorOverlay} maykaPlpUtmSourceKeyword={headerContent?.maykaPlpUtmSourceKeyword} />
      )}

      <NavCtx.Provider
        value={{
          isAtTop,
          isHomePage,
          isScrolling,
          animation: ANIMATION,
          duration: DURATION,
          handleIsLinkHovered,
          isLinkHovered,
          dynamicHeader: isDynamicHeaderShown,
          navData: headerContent,
        }}
      >
        {isDynamicHeaderShown ? (
          <DynamicBanner content={dynamicBanner} locale={locale} />
        ) : (
          // <CountdownBanner
          //   countdownDate={countdownDate}
          //   content="Up to 40% site wise, this weekend only"
          //   feel={{ bannerClassName: "bg-[rgba(194,212,220,1)]" }}
          // />
          <></>
        )}
        {!isHomePage ? (
          <div
            className={`w-full ${isDynamicHeaderShown ? "h-[154px] md:h-[190px]" : "h-[108px] md:h-[140px]"}`}
          ></div>
        ) : (
          <></>
        )}
        <header
          id="nav-header"
          style={
            {
              // fontSize: dynamicBanners?.fontSize ?? "",
              // ...(is29LTBukraFont
              //   ? fontLTBukra.style
              //   : {
              //       fontFamily: dynamicBanners?.fontFace
              //         ? `"${dynamicBanners.fontFace}", sans-serif`
              //         : "",
              //     }),
            }
          }
          className={cn(
            "fixed top-0 z-20 w-full bg-white text-black transition-[height,background-color]",
            handleGetHeaderClasses(),
            ANIMATION,
            DURATION,
            isDynamicHeaderShown ? "top-[40px] md:top-[50px]" : "",
            isLinkHovered || showSearch || showMobileSearch ? "bg-white text-black" : "",
          )}
        >
          <div
            id="nav-header-bg"
            className={cn(
              "fixed left-0 top-0 -z-10 w-full bg-black-to-white-opacity",
              handleGetHeaderBgClasses(),
              isDynamicHeaderShown ? "top-[40px] md:top-[50px]" : "",
              isLinkHovered || showSearch || showMobileSearch ? "hidden" : "",
            )}
          ></div>

          <nav className="container md:px-12">
            <div
              id="nav-upper-links"
              className={cn("relative flex items-center gap-4 px-4", handleGetNavUpperClasses())}
            >
              <div className="flex h-full flex-1 items-center gap-5">
                <HamIcon
                  onClick={handleShowMobileNav}
                  className="lg:hidden"
                  //@ts-ignore
                  title={getFieldValue(lang, "Menu", "قائمة طعام")}
                />
                <button className="lg:hidden" onClick={() => setShowMobileSearch(true)}>
                  <SearchIcon
                    //@ts-ignore
                    title={getFieldValue(lang, "Search", "يبحث")}
                  />
                </button>
                {headerModules?.map((mod, idx) => (
                  <CLink
                    key={idx}
                    href={mod.link?.url ?? "/"}
                    className="hidden font-medium lg:inline xl:whitespace-nowrap"
                  >
                    {lang == "en" ? mod?.title?.en : mod?.title?.ar}
                  </CLink>
                ))}
              </div>
              <OnceAnimatedBrandLogo />
              <div
                className={cn(
                  "flex h-full flex-1 items-center justify-end",
                  !showSearch ? "gap-7" : "",
                )}
              >
                <div
                  className={cn(
                    "hidden items-center overflow-x-hidden transition-[opacity] duration-300 lg:flex",
                    showSearch ? "w-96 opacity-100" : "w-0 opacity-0",
                  )}
                >
                  <SearchBar
                    open={showSearch}
                    closeOnBlur
                    suggestionRef={suggestionRef}
                    onClose={() => setShowSearch(false)}
                    onChange={debouncedSearch}
                  />
                </div>
                <div
                  className={cn(
                    "flex items-center justify-end gap-7 transition-all duration-300",
                    !showSearch ? "opacity-100" : "pointer-events-none w-0 opacity-0",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setShowSearch(true)}
                    className="hidden lg:block"
                  >
                    <SearchIcon
                      //@ts-ignore
                      title={getFieldValue(lang, "Search", "يبحث")}
                    />
                  </button>
                  <Suspense>
                    <div className="hidden lg:block">
                      <NavReorderLenses translation={translation} />
                    </div>
                  </Suspense>
                  <CLink href={isAuthenticated && user ? "/my-account/favorites" : "/favorites"}>
                    <FavoriteIcon
                      //@ts-ignore
                      title={getFieldValue(lang, "Favourites", "المفضلة")}
                    />
                  </CLink>
                  <div role="button" className="hidden lg:block">
                    <Suspense fallback={<></>}>
                      <ProfileIcon
                        usePrimaryColor={isPrimaryTextColorEnabled()}
                        enableAppleLogin={headerContent?.enableAppleLogin ?? false}
                        enableFacebookLogin={headerContent?.enableFacebookLogin ?? false}
                        enableGoogleLogin={headerContent?.enableGoogleLogin ?? false}
                        // onClick={handleHideMobileNav}
                      />
                    </Suspense>
                  </div>
                  {!isNonTransactional && (
                    <CLink href="/cart" className="relative">
                      <CartIcon
                        style={{
                          width: "1.5625rem",
                        }}
                        //@ts-ignore
                        title={getFieldValue(lang, "Cart", "عربة")}
                      />
                      {cartCount > 0 && (
                        <span className="absolute -end-3 -top-3 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-black text-sm text-white">
                          {cartCount}
                        </span>
                      )}
                    </CLink>
                  )}
                </div>
              </div>
            </div>
            <DesktopSuggesationMenu
              open={showSearch}
              searchTerm={searchTerm}
              locale={locale as string}
              lang={lang}
              translation={translation}
              suggestionRef={suggestionRef}
              onClose={() => setShowSearch(false)}
              enableVto={enableVto}
            />

            <Suspense fallback={<></>}>
              <NavMobileMenu
                open={showMobileMav}
                onClose={handleHideMobileNav}
                headerContent={headerContent}
                socialMediaIcons={socialMediaIcons ?? []}
                translation={translation}
              />
            </Suspense>
            <NavDesktopMenu
              hideFlyMenu={hideFlyMenu}
              handleSetFlyMenuState={handleSetFlyMenuState}
            />
            <MobileSearchMenu
              open={showMobileSearch}
              locale={locale as string}
              lang={lang}
              translation={translation}
              onClose={() => setShowMobileSearch(false)}
              enableVto={enableVto}
            />
          </nav>
        </header>
      </NavCtx.Provider>
    </>
  );
};

export default Navbar;
